import React from 'react'
import PropTypes from 'prop-types'
import './chip-input.css'

const DUP_NOTICE_MS = 2000

const norm = value => value.trim().toLowerCase()

/**
 * A chip / tag input. Typed text (which may contain spaces and commas) becomes
 * a chip only when the user presses Enter; nothing auto-splits while typing.
 * Backspace on an empty field removes the last chip; each chip has an × button.
 * Case-insensitive duplicates are not added (a transient notice is shown).
 * Pasting splits the clipboard text per `splitMode` (lines / commas / none).
 * The value is a string[]; the parent owns it.
 */
export class ChipInput extends React.Component {
  constructor (props) {
    super(props)

    this.state = { draft: '', dupNotice: false }
    this.dupTimer = null

    this.handleDraftChange = this.handleDraftChange.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handlePaste = this.handlePaste.bind(this)
  }

  componentWillUnmount () {
    if (this.dupTimer) clearTimeout(this.dupTimer)
  }

  flashDuplicate () {
    this.setState({ dupNotice: true })
    if (this.dupTimer) clearTimeout(this.dupTimer)
    this.dupTimer = setTimeout(
      () => this.setState({ dupNotice: false }),
      DUP_NOTICE_MS
    )
  }

  /**
   * Adds a single word. Returns true when added, false when empty/duplicate.
   *
   * @param {string} raw The raw text to commit
   * @returns {boolean} Whether the word was added
   */
  commit (raw) {
    const word = raw.trim()
    if (!word) return false

    const exists = this.props.value.some(item => norm(item) === norm(word))
    if (exists) {
      this.flashDuplicate()
      return false
    }

    this.props.onChange([...this.props.value, word])
    return true
  }

  handleDraftChange (event) {
    this.setState({ draft: event.target.value })
  }

  handleKeyDown (event) {
    const { draft } = this.state

    if (event.key === 'Enter') {
      event.preventDefault()
      if (this.commit(draft)) this.setState({ draft: '' })
      return
    }

    if (
      event.key === 'Backspace' &&
      draft === '' &&
      this.props.value.length > 0
    ) {
      event.preventDefault()
      this.removeAt(this.props.value.length - 1)
    }
  }

  splitPaste (text) {
    switch (this.props.splitMode) {
      case 'commas':
        return text.split(/[\n,]/)
      case 'none':
        return [text]
      case 'lines':
      default:
        return text.split('\n')
    }
  }

  handlePaste (event) {
    const text =
      event.clipboardData && event.clipboardData.getData
        ? event.clipboardData.getData('text')
        : null
    if (text == null) return

    event.preventDefault()

    const seen = new Set(this.props.value.map(norm))
    const fresh = []
    let hadDuplicate = false

    this.splitPaste(text).forEach(item => {
      const word = item.trim()
      if (!word) return
      if (seen.has(norm(word))) {
        hadDuplicate = true
        return
      }
      seen.add(norm(word))
      fresh.push(word)
    })

    if (fresh.length) this.props.onChange([...this.props.value, ...fresh])
    if (hadDuplicate) this.flashDuplicate()
  }

  removeAt (index) {
    const next = this.props.value.slice()
    next.splice(index, 1)
    this.props.onChange(next)
  }

  render () {
    const { value, disabled, label, subTitle, tooltip, inputId } = this.props
    const { draft, dupNotice } = this.state
    const count = value.length

    return (
      <div className='br-chip-section' style={{ marginTop: '15px' }}>
        <div className='br-chip-label-row'>
          <label className='control-label' htmlFor={inputId}>
            {label}
          </label>
          <button
            type='button'
            className='br-info-icon'
            aria-label={tooltip}
            data-tooltip={tooltip}
          />
        </div>
        <p className='br-list-info'>{subTitle}</p>

        <div
          className={
            'br-chip-input form-control' +
            (disabled ? ' br-chip-input--disabled' : '')
          }
        >
          {value.map((chip, index) => (
            <span className='br-chip' key={`${chip}-${index}`}>
              <span className='br-chip-label'>{chip}</span>
              <button
                type='button'
                className='br-chip-remove'
                aria-label={`Remove ${chip}`}
                disabled={disabled}
                onClick={() => this.removeAt(index)}
              >
                ×
              </button>
            </span>
          ))}
          <input
            id={inputId}
            className='br-chip-field'
            type='text'
            placeholder='type a word and press Enter…'
            value={draft}
            disabled={disabled}
            onChange={this.handleDraftChange}
            onKeyDown={this.handleKeyDown}
            onPaste={this.handlePaste}
          />
          {dupNotice && (
            <span className='br-chip-dup-notice' role='status'>
              Already added
            </span>
          )}
        </div>

        <p className='help-block br-chip-count'>
          You've entered {count} {count === 1 ? 'word' : 'words'}
        </p>
      </div>
    )
  }
}

ChipInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  subTitle: PropTypes.string,
  tooltip: PropTypes.string,
  inputId: PropTypes.string,
  splitMode: PropTypes.oneOf(['lines', 'commas', 'none'])
}

ChipInput.defaultProps = {
  value: [],
  splitMode: 'lines',
  onChange: () => {}
}

export default ChipInput



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-filtering/chip-input.js